import { useState, useMemo } from 'react';
import { useDebounce, useUserPermissionSearch, useUpsertUserPermission } from '../../hooks';
import { useEvoContext } from '../../hooks/useEvoContext';
import type { UserPermissionRecord, InAppRole } from '../../api/user-permissions';
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Spinner } from '../../components/ui/spinner';
import { Input } from '../../components/ui/input';
import { Save, Search, X, AlertCircle, Info } from 'lucide-react';
import { formatDateTimeUK } from '../../lib/date-utils';
import { toast } from 'sonner';

const IN_APP_ROLES: { value: InAppRole; label: string }[] = [
  { value: 'ReadOnly', label: 'Read-only' },
  { value: 'Approver', label: 'Approver' },
  { value: 'Administrator', label: 'Administrator' },
];

const ROLE_LABELS: Record<string, string> = {
  ReadOnly: 'Read-only',
  Approver: 'Approver',
  Administrator: 'Administrator',
  NoAccess: 'No Access',
};

/**
 * Derives the effective role for a user from the stored permission list, mirroring the
 * backend precedence (Administrator > Approver > ReadOnly > NoAccess).
 */
function getEffectiveRole(permissions: string[]): string {
  if (permissions.includes('Administrator')) return 'Administrator';
  if (permissions.includes('Approver')) return 'Approver';
  if (permissions.includes('ReadOnly')) return 'ReadOnly';
  return 'NoAccess';
}

export function MembersManagementContent() {
  const upsertMutation = useUpsertUserPermission();
  const { user: evoUser } = useEvoContext();
  const currentEmail = evoUser?.emailAddress?.toLowerCase() ?? null;

  const [edits, setEdits] = useState<Record<string, InAppRole>>({});
  const [savingEmail, setSavingEmail] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const debouncedSearch = useDebounce(searchQuery.trim(), 300);
  const hasSearch = debouncedSearch.length > 0;

  const { data: users = [], isFetching } = useUserPermissionSearch(debouncedSearch);

  const filteredUsers = useMemo(() => {
    if (roleFilter === 'all') return users;
    return users.filter((u) => getEffectiveRole(u.permissions) === roleFilter);
  }, [users, roleFilter]);

  function isSelf(email: string): boolean {
    return currentEmail !== null && email.toLowerCase() === currentEmail;
  }

  function getRowRole(user: UserPermissionRecord): string {
    return edits[user.user_email] ?? getEffectiveRole(user.permissions);
  }

  function handleRoleChange(email: string, value: InAppRole) {
    setEdits((prev) => ({ ...prev, [email]: value }));
  }

  async function handleSave(user: UserPermissionRecord) {
    const role = edits[user.user_email];
    if (!role) return;

    setSavingEmail(user.user_email);
    try {
      await upsertMutation.mutateAsync({
        user_email: user.user_email,
        name: user.name,
        permissions: [role],
      });
      setEdits((prev) => {
        const next = { ...prev };
        delete next[user.user_email];
        return next;
      });
      toast.success(`Updated role for ${user.name || user.user_email}`);
    } catch {
      toast.error(`Failed to update role for ${user.name || user.user_email}`);
    } finally {
      setSavingEmail(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground mt-1">
          Manage in-app roles for users who already have access via Evo Members. Roles here
          elevate a user above the default Read-only access.
        </p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Application access (on/off) is controlled by Evo Members. A user granted access
          defaults to <strong>Read-only</strong>; use this area to elevate them to Approver or
          Administrator.
        </AlertDescription>
      </Alert>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Search Member</label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 w-[260px]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                type="button"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium">Role</label>
          <div className="flex items-center gap-1">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {IN_APP_ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {roleFilter !== 'all' && (
              <button onClick={() => setRoleFilter('all')} className="p-1 text-muted-foreground hover:text-foreground" type="button">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {hasSearch && !isFetching && (
          <span className="text-sm text-muted-foreground self-end pb-2">
            Showing {filteredUsers.length} {filteredUsers.length === 1 ? 'member' : 'members'}
          </span>
        )}
      </div>

      {!hasSearch ? (
        <div className="rounded-md border border-dashed p-12 text-center text-muted-foreground">
          <Search className="mx-auto h-8 w-8 mb-3 opacity-50" />
          <p className="text-sm">Search by name or email to manage a member's role.</p>
        </div>
      ) : isFetching ? (
        <div className="flex items-center justify-center p-12">
          <Spinner className="h-8 w-8" />
          <span className="ml-3 text-muted-foreground">Searching members...</span>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[220px]">Name</TableHead>
                <TableHead className="w-[260px]">Email</TableHead>
                <TableHead className="w-[180px]">Role</TableHead>
                <TableHead className="w-[160px]">Last Updated</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((user) => {
                const effectiveRole = getEffectiveRole(user.permissions);
                const currentRole = getRowRole(user);
                const hasEdit = user.user_email in edits;
                const isNoAccess = effectiveRole === 'NoAccess';
                const self = isSelf(user.user_email);

                return (
                  <TableRow key={user.user_email}>
                    <TableCell className="font-medium">
                      {user.name || '-'}
                      {self && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground truncate max-w-[260px]">
                      {user.user_email}
                    </TableCell>
                    <TableCell>
                      {isNoAccess ? (
                        <Badge variant="outline" className="text-muted-foreground">
                          {ROLE_LABELS.NoAccess}
                        </Badge>
                      ) : self ? (
                        <div className="flex flex-col gap-1">
                          <Badge variant="secondary">{ROLE_LABELS[effectiveRole]}</Badge>
                          <span className="text-xs text-muted-foreground">You cannot change your own role.</span>
                        </div>
                      ) : (
                        <Select
                          value={currentRole}
                          onValueChange={(v) => handleRoleChange(user.user_email, v as InAppRole)}
                        >
                          <SelectTrigger className="w-[160px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {IN_APP_ROLES.map((r) => (
                              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {user.updated_at ? formatDateTimeUK(user.updated_at) : '-'}
                    </TableCell>
                    <TableCell>
                      {!isNoAccess && !self && (
                        <Button
                          size="sm"
                          disabled={!hasEdit || savingEmail === user.user_email}
                          onClick={() => handleSave(user)}
                        >
                          {savingEmail === user.user_email ? (
                            <Spinner className="h-4 w-4" />
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-1" /> Save
                            </>
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    <span className="inline-flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" /> No members match your search.
                    </span>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
